import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// Icon Components - Simple and Clean
const RecordIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="10"/>
    <circle cx="12" cy="12" r="3" fill="#000"/>
  </svg>
);

const PersonIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
  </svg>
);

const CalendarIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7z"/>
  </svg>
);

const StarIcon = () => (
  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    episodes: { total: 0, featured: 0 },
    guests: { total: 0, topIndustries: [] },
    upcoming: { total: 0, anonymous: 0, public: 0 },
    sponsors: { total: 0, active: 0, inactive: 0 }
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      
      const endpoints = [
        '/api/episodes/?size=1',
        '/api/guests/stats/summary',
        '/api/upcoming/stats/summary',
        '/api/sponsors/stats/summary'
      ];

      const responses = await Promise.all(
        endpoints.map(endpoint => 
          fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          })
        )
      );

      const [episodesRes, guestsRes, upcomingRes, sponsorsRes] = await Promise.all(
        responses.map(res => res.json())
      );

      setStats({
        episodes: {
          total: episodesRes.total || 0,
          featured: episodesRes.items?.filter(ep => ep.is_featured).length || 0
        },
        guests: guestsRes,
        upcoming: upcomingRes,
        sponsors: sponsorsRes
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: IconComponent, color = 'electric-yellow' }) => (
    <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-6 hover:scale-105 transition-transform duration-300">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-bold text-soft-gray">{title}</h3>
        <div className="text-electric-yellow">
          <IconComponent />
        </div>
      </div>
      <div className={`text-3xl font-bold text-${color} mb-2`}>
        {loading ? '...' : value}
      </div>
      {subtitle && (
        <p className="text-soft-gray text-sm">{subtitle}</p>
      )}
    </div>
  );

  const QuickActions = () => (
    <div className="bg-charcoal/40 backdrop-blur-sm border border-slate-dark/30 rounded-2xl p-4 md:p-6">
      <h3 className="font-bold text-electric-yellow mb-4 md:mb-6">Quick Actions</h3>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
        <button 
          onClick={() => navigate('/admin/episodes/create')}
          className="flex flex-col items-center gap-3 p-4 bg-midnight/60 rounded-xl hover:bg-electric-yellow/10 transition-colors duration-300"
        >
          <div className="text-electric-yellow">
            <RecordIcon />
          </div>
          <span className="text-sm font-medium text-center">Add New Episode</span>
        </button>
        <button 
          onClick={() => navigate('/admin/guests/create')}
          className="flex flex-col items-center gap-3 p-4 bg-midnight/60 rounded-xl hover:bg-electric-yellow/10 transition-colors duration-300"
        >
          <div className="text-electric-yellow">
            <PersonIcon />
          </div>
          <span className="text-sm font-medium text-center">Add New Guest</span>
        </button>
        <button 
          onClick={() => navigate('/admin/upcoming/create')}
          className="flex flex-col items-center gap-3 p-4 bg-midnight/60 rounded-xl hover:bg-electric-yellow/10 transition-colors duration-300"
        >
          <div className="text-electric-yellow">
            <CalendarIcon />
          </div>
          <span className="text-sm font-medium text-center">Add Upcoming Episode</span>
        </button>
        <button 
          onClick={() => navigate('/admin/sponsors/create')}
          className="flex flex-col items-center gap-3 p-4 bg-midnight/60 rounded-xl hover:bg-electric-yellow/10 transition-colors duration-300"
        >
          <div className="text-electric-yellow">
            <StarIcon />
          </div>
          <span className="text-sm font-medium text-center">Add New Sponsor</span>
        </button>
      </div>
    </div>
  );

  return (
    <div className="space-y-8">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-deep-teal/20 to-electric-yellow/10 backdrop-blur-sm border border-electric-yellow/20 rounded-3xl p-6 md:p-8">
        <h2 className="font-clash text-2xl md:text-3xl font-bold text-electric-yellow mb-4">
          Welcome to Unframed Talks CMS
        </h2>
        <p className="text-soft-gray text-base md:text-lg">
          Manage your podcast content, guests, and sponsors all in one place. 
          Your content management system for authentic conversations.
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Episodes"
          value={stats.episodes.total}
          subtitle={`${stats.episodes.featured} featured`}
          icon={RecordIcon}
        />
        <StatCard
          title="Total Guests"
          value={stats.guests.total_guests}
          subtitle={`${stats.guests.top_industries?.length || 0} industries`}
          icon={PersonIcon}
        />
        <StatCard
          title="Upcoming Episodes"
          value={stats.upcoming.total_upcoming}
          subtitle={`${stats.upcoming.anonymous_count} anonymous`}
          icon={CalendarIcon}
        />
        <StatCard
          title="Active Sponsors"
          value={stats.sponsors.active_sponsors}
          subtitle={`${stats.sponsors.total_sponsors} total`}
          icon={StarIcon}
        />
      </div>

      {/* Quick Actions */}
      <QuickActions />
    </div>
  );
};

export default AdminDashboard;